import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  FaHardHat,
  FaHome,
  FaPowerOff,
  FaSignInAlt,
  FaUserShield,
  FaTools,
  FaClipboardList,
  FaBars,
  FaTimes,
  FaBell,
  FaMobileAlt,
} from 'react-icons/fa';
import api from '../services/api';
import socket from '../socket_';
import documentService from '../services/documentService';
import abonnementService from '../services/abonnementService';
import ProfileModal from './ProfileModal';
import { CrescentStar, Lantern, ZelligeBg, ZelligeCorner, AID } from './AidMubarak/AidOrnaments';
import { TwinkleStars, GoldParticles, useShimmerAnim, useSwayAnim } from './AidMubarak/AidAnimations';

const shimmerBg = `linear-gradient(90deg, ${AID.goldDeep}, ${AID.goldLight}, ${AID.gold}, ${AID.goldDeep})`;

const Navbar = ({ user, onLogout, aidActive }) => {
  const navigate = useNavigate();
  const [menuOpen,        setMenuOpen]        = useState(false);
  const [showProfile,     setShowProfile]     = useState(false);
  const [photoProfilId,   setPhotoProfilId]   = useState(null);
  const [avatarUrl,       setAvatarUrl]       = useState(null);
  const [nbAlertes,       setNbAlertes]       = useState(0);
  const [nbDevicesPending, setNbDevicesPending] = useState(0);
  const titleRef = useRef(null);
  const lanternL = useRef(null);
  const lanternR = useRef(null);

  const showBanner = aidActive && user;

  useShimmerAnim(titleRef, !showBanner);
  const animL = useSwayAnim(lanternL, 3500, !showBanner);
  const animR = useSwayAnim(lanternR, 4100, !showBanner);

  useEffect(() => {
    if (!user?.id) { setPhotoProfilId(null); return; }
    api.get('/users/me')
      .then(r => setPhotoProfilId(r.data.photo_profil || null))
      .catch(() => {});
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) { setNbAlertes(0); return; }
    abonnementService.getAlertes()
      .then(r => {
        const today = new Date().toISOString().split('T')[0];
        const proches = (r.data || []).filter(e => {
          const j = Math.ceil((new Date(e.date_echeance) - new Date(today)) / 86400000);
          return j <= (e.abonnement?.alerte_jours ?? 7);
        });
        setNbAlertes(proches.length);
      })
      .catch(() => {});
  }, [user?.id]);

  // Admin : appareils en attente de validation, rafraîchi en temps réel via Socket.io
  useEffect(() => {
    if (user?.role !== 'admin') { setNbDevicesPending(0); return; }
    const loadCount = () => api.get('/user_devices/pending-count')
      .then(r => setNbDevicesPending(r.data?.count || 0))
      .catch(() => {});
    loadCount();
    socket.on('devices:pending-changed', loadCount);
    return () => socket.off('devices:pending-changed', loadCount);
  }, [user?.role]);

  useEffect(() => {
    let revoked = false;
    let objectUrl = null;
    if (photoProfilId) {
      documentService.getDocumentObjectUrl(photoProfilId)
        .then(url => {
          if (revoked) { URL.revokeObjectURL(url); return; }
          objectUrl = url;
          setAvatarUrl(url);
        })
        .catch(() => setAvatarUrl(null));
    } else {
      setAvatarUrl(null);
    }
    return () => { revoked = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [photoProfilId]);

  const handlePhotoChanged = (newId) => setPhotoProfilId(newId);

  const roleStyles = {
    admin:              { label: 'Admin',            color: '#dc3545', icon: <FaUserShield /> },
    responsable:        { label: 'Responsable',      color: '#ffc107', icon: <FaTools /> },
    pointeur:           { label: 'Pointeur',         color: '#0d6efd', icon: <FaClipboardList /> },
    responsable_budget: { label: 'Resp. Budget',     color: '#6f42c1', icon: <FaClipboardList /> },
    achat:              { label: 'Achat',            color: '#0d9488', icon: <FaClipboardList /> },
    resp_commercial:    { label: 'Resp. Commercial', color: '#e91e63', icon: <FaClipboardList /> }
  };

  const styleNavbar = showBanner ? {
    background: 'linear-gradient(180deg, #fdf3d0 0%, #f5e070 65%, #e8c94a 100%)',
    borderBottom: `2px solid ${AID.goldDeep}80`,
    zIndex: 1000,
    padding: 0,
    overflow: 'hidden',
  } : {
    backgroundColor: '#f5f5db',
    borderBottom: '2px solid #6d4e36',
    zIndex: 1000,
    padding: 0,
  };

  const accentColor  = showBanner ? AID.brownDeep : '#6d4e36';
  const styleLink    = { color: accentColor, fontWeight: 'bold', transition: 'transform 0.2s ease-in-out' };
  const styleHomeBtn = { border: `1px solid ${accentColor}`, backgroundColor: 'transparent', color: accentColor };

  const initials = (user?.name || '?')
    .split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

  const AvatarCircle = () => (
    <div
      onClick={() => setShowProfile(true)}
      title="Mon profil"
      style={{
        width: 34, height: 34, borderRadius: '50%',
        border: `2px solid ${accentColor}`, overflow: 'hidden',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        backgroundColor: '#e8ddd4', cursor: 'pointer', flexShrink: 0,
        fontSize: 13, fontWeight: 'bold', color: accentColor,
        transition: 'box-shadow 0.15s', boxShadow: '0 1px 3px rgba(0,0,0,0.15)'
      }}
      onMouseEnter={e => (e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.25)')}
      onMouseLeave={e => (e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.15)')}
    >
      {avatarUrl
        ? <img src={avatarUrl} alt="profil" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        : initials}
    </div>
  );

  return (
    <>
      <nav className="navbar shadow-sm fixed-top" style={styleNavbar}>

        {/* CSS hover lanternes */}
        {showBanner && (
          <style>{`
            .aid-lantern-wrap {
              cursor: default;
              transition: filter 0.3s ease;
            }
            .aid-lantern-wrap:hover {
              filter: brightness(1.5) drop-shadow(0 0 10px #e8c662) drop-shadow(0 0 4px #a8821a);
            }
          `}</style>
        )}

        {/* ── Décors festifs en fond (uniquement en mode Aid) ── */}
        {showBanner && (
          <>
            <ZelligeBg color={AID.goldDeep} opacity={0.08} />
            <TwinkleStars count={14} />
            <GoldParticles count={8} />
            <div style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none', opacity: 0.22 }}>
              <ZelligeCorner size={90} color={AID.goldDeep} opacity={1} flip="tl" />
            </div>
            <div style={{ position: 'absolute', top: 0, right: 0, pointerEvents: 'none', opacity: 0.22 }}>
              <ZelligeCorner size={90} color={AID.goldDeep} opacity={1} flip="tr" />
            </div>
            <div style={{
              position: 'absolute', bottom: 0, left: 0, right: 0,
              height: 3, pointerEvents: 'none',
              background: `linear-gradient(90deg, transparent, ${AID.gold}, ${AID.goldLight}, ${AID.gold}, transparent)`,
            }} />
          </>
        )}

        {/* ── Ligne principale : Logo | Aid centre | Actions ── */}
        <div
          className="container px-3 d-flex align-items-center flex-wrap"
          style={{
            maxWidth: '1300px', margin: '0 auto',
            paddingTop: 6,
            paddingBottom: 6,
            position: 'relative', zIndex: 1,
          }}
        >
          {/* Logo */}
          <Link
            className="navbar-brand d-flex align-items-center gap-2 flex-shrink-0"
            to="/"
            style={styleLink}
            onMouseEnter={e => (e.currentTarget.style.transform = 'scale(1.05)')}
            onMouseLeave={e => (e.currentTarget.style.transform = 'scale(1)')}
          >
            <FaHardHat size={22} />
            <span>PAIESHEMS</span>
          </Link>

          {/* ── Contenu Aid au centre (desktop uniquement) ── */}
          {showBanner ? (
            <div className="d-none d-md-flex align-items-center justify-content-center flex-grow-1" style={{ gap: 10 }}>
              <div className="aid-lantern-wrap"
                onMouseEnter={() => animL.current && (animL.current.playbackRate = 2.8)}
                onMouseLeave={() => animL.current && (animL.current.playbackRate = 1)}>
                <div ref={lanternL} style={{ transformOrigin: 'top center', flexShrink: 0 }}>
                  <Lantern size={34} color={AID.goldDeep} glowColor={AID.goldLight} />
                </div>
              </div>
              <CrescentStar size={22} color={AID.goldDeep} glow />
              <div style={{ textAlign: 'center' }}>
                <div ref={titleRef} dir="rtl" style={{
                  fontFamily: "'Amiri', 'Georgia', serif",
                  fontSize: 22,
                  fontWeight: 700,
                  background: shimmerBg,
                  backgroundSize: '200% 100%',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  lineHeight: 1,
                  userSelect: 'none',
                }}>
                  عيد الأضحى مبارك
                </div>
                <div style={{
                  fontFamily: "'Reem Kufi', 'Arial', sans-serif",
                  fontSize: 10,
                  color: AID.brownDeep,
                  fontWeight: 700,
                  letterSpacing: 3,
                  userSelect: 'none',
                  marginTop: 2,
                }}>
                  AID MOUBARAK SAÏD
                  {user?.login && (
                    <span style={{ color: AID.goldDeep, marginLeft: 6, letterSpacing: 1 }}>— {user.login}</span>
                  )}
                </div>
              </div>
              <CrescentStar size={22} color={AID.goldDeep} glow />
              <div className="aid-lantern-wrap"
                onMouseEnter={() => animR.current && (animR.current.playbackRate = 2.8)}
                onMouseLeave={() => animR.current && (animR.current.playbackRate = 1)}>
                <div ref={lanternR} style={{ transformOrigin: 'top center', flexShrink: 0 }}>
                  <Lantern size={34} color={AID.goldDeep} glowColor={AID.goldLight} />
                </div>
              </div>
            </div>
          ) : (
            <div className="flex-grow-1 d-none d-md-block" />
          )}

          {/* Desktop actions */}
          <div className="d-none d-md-flex align-items-center gap-2 flex-shrink-0">
            <button className="btn btn-sm" style={styleHomeBtn} onClick={() => navigate('/')} title="Accueil">
              <FaHome />
            </button>
            {user ? (
              <>
                {nbAlertes > 0 && (
                  <button className="btn btn-sm position-relative" style={{ ...styleHomeBtn, color: '#fd7e14', borderColor: '#fd7e14' }}
                    onClick={() => navigate('/abonnements')} title="Abonnements — échéances proches">
                    <FaBell />
                    <span style={{
                      position: 'absolute', top: -4, right: -4,
                      background: '#dc3545', color: '#fff',
                      borderRadius: '50%', width: 16, height: 16,
                      fontSize: '0.65rem', fontWeight: 700,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      lineHeight: 1,
                    }}>{nbAlertes > 9 ? '9+' : nbAlertes}</span>
                  </button>
                )}
                {nbDevicesPending > 0 && (
                  <button className="btn btn-sm position-relative" style={{ ...styleHomeBtn, color: '#ef6c00', borderColor: '#ef6c00' }}
                    onClick={() => navigate('/user-devices')} title="Appareils en attente de validation">
                    <FaMobileAlt />
                    <span style={{
                      position: 'absolute', top: -4, right: -4,
                      background: '#dc3545', color: '#fff',
                      borderRadius: '50%', width: 16, height: 16,
                      fontSize: '0.65rem', fontWeight: 700,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      lineHeight: 1,
                    }}>{nbDevicesPending > 9 ? '9+' : nbDevicesPending}</span>
                  </button>
                )}
                <AvatarCircle />
                <span className="navbar-text text-dark d-flex align-items-center gap-2 ms-1">
                  <strong style={{ color: accentColor }}>{user.name}</strong>
                  <span className="badge rounded-pill"
                    style={{ backgroundColor: roleStyles[user.role]?.color || '#999', color: 'white' }}>
                    {roleStyles[user.role]?.label}
                  </span>
                </span>
                <button onClick={onLogout} className="btn btn-outline-danger btn-sm ms-1" title="Se déconnecter">
                  <FaPowerOff />
                </button>
              </>
            ) : (
              <Link to="/login" className="btn btn-outline-success btn-sm">
                <FaSignInAlt /> Se connecter
              </Link>
            )}
          </div>

          {/* Hamburger (mobile) */}
          <button className="btn btn-sm d-md-none ms-auto" style={styleHomeBtn}
            onClick={() => setMenuOpen(p => !p)} aria-label="Menu">
            {menuOpen ? <FaTimes /> : <FaBars />}
          </button>

          {/* Menu mobile déplié */}
          {menuOpen && (
            <div className="w-100 d-md-none d-flex flex-column gap-2 pb-2 pt-2 border-top mt-2"
              style={{ borderColor: showBanner ? `${AID.goldDeep}50` : '#d8c8b8' }}>
              <button className="btn btn-sm w-100 text-start" style={styleHomeBtn}
                onClick={() => { navigate('/'); setMenuOpen(false); }}>
                <FaHome className="me-2" /> Accueil
              </button>
              {user ? (
                <>
                  <div className="d-flex align-items-center gap-2 px-1">
                    <AvatarCircle />
                    <strong style={{ color: accentColor }}>{user.name}</strong>
                    <span className="badge rounded-pill"
                      style={{ backgroundColor: roleStyles[user.role]?.color || '#999', color: 'white' }}>
                      {roleStyles[user.role]?.label}
                    </span>
                  </div>
                  <button onClick={() => { onLogout(); setMenuOpen(false); }}
                    className="btn btn-outline-danger btn-sm w-100 text-start">
                    <FaPowerOff className="me-2" /> Se déconnecter
                  </button>
                </>
              ) : (
                <Link to="/login" className="btn btn-outline-success btn-sm"
                  onClick={() => setMenuOpen(false)}>
                  <FaSignInAlt className="me-2" /> Se connecter
                </Link>
              )}
            </div>
          )}
        </div>
      </nav>

      {/* Spacer supplémentaire pour compenser la hauteur du contenu festif dans la navbar */}
      {showBanner && <div style={{ height: 10 }} />}

      {user && (
        <ProfileModal
          show={showProfile}
          onHide={() => setShowProfile(false)}
          user={user}
          photoProfilId={photoProfilId}
          onPhotoChanged={handlePhotoChanged}
        />
      )}
    </>
  );
};

export default React.memo(Navbar);
